/**
 * PurchaseOrderDetailPage — one purchase order by its number,
 * `/purchase-orders/:purchaseOrderNo` and `/purchase-orders/:purchaseOrderNo/:tab`:
 * purchasing's view (docs/features/order-to-invoice/
 * 08_order_pages_frontend_spec.md §5.4). CSRs see its line on the order.
 *
 * The order page's pattern: the header (the value is cost, never price), its
 * own rail — Raised · Placed · Despatched · Delivered / Received · Billed ·
 * Matched — then tabs: Now · Details · Lines · Receipts · Purchase invoices · Activity. A
 * purchase order can span orders: each line is "for" an order line (a
 * restock is an order line delivering into a stock item). Record delivered sits on the Receipts rows and
 * posts to the order's own route, through the URL the view carries. The ⋯ menu
 * holds Record despatch (once placed), Record cancellation and, while it is held
 * for price approval, Approve price (a manager or admin), each posting to
 * purchasing's own route as the view carries it.
 *
 * Staff with `purchase_orders:view` (every role that reads orders). Out of
 * the caller's scope reads as not found. Every label and sentence is the
 * view's (docs/features/order-to-invoice/16_frontend_followup_spec.md §2).
 */
import { MoreHorizontal } from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useParams } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { recordCommand } from "@shared-core/data/order-to-invoice-actions";
import type {
  ApprovePurchaseOrderPriceRequest,
  DeliveryLineView,
  PurchaseOrderCommand,
  PurchaseOrderLineView,
  PurchaseOrderView,
  RecordAgreedAmountRequest,
  RecordPurchaseOrderCancellationRequest,
  RecordPurchaseOrderDespatchRequest,
  SupplierPurchaseInvoice,
  SupplierProofView,
} from "@shared-core/data/order-to-invoice-types";
import { despatchProgressStatus } from "@shared-core/domain/despatch-progress";
import { railFromPurchaseOrder } from "@shared-core/domain/order-lifecycle-rail";
import {
  nowBadge,
  orderStepFailure,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  PURCHASE_ORDER_TABS,
  paths,
  type PurchaseOrderTab,
} from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Card, CardContent } from "../app/components/primitives/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../app/components/primitives/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { InfoTip } from "../app/components/patterns/InfoTip";
import { ObjectChip } from "../app/components/patterns/ObjectChip";
import { LifecycleRail } from "../app/components/patterns/LifecycleRail";
import { ObjectHeader } from "../app/components/patterns/ObjectHeader";
import { ObjectTabs } from "../app/components/patterns/ObjectTabs";
import {
  DeliveryLinesTable,
  LineCompletionDialog,
  type LineCompletion,
} from "../app/components/domain/orders/DeliveryLinesTable";
import { LineDespatches } from "../app/components/domain/orders/LineDespatches";
import { OrderNowPanel } from "../app/components/domain/orders/OrderNow";
import { ApprovePriceDialog } from "../app/components/domain/orders/ApprovePriceDialog";
import { RecordAgreedAmountDialog } from "../app/components/domain/orders/RecordAgreedAmountDialog";
import { RecordCancellationDialog } from "../app/components/domain/orders/RecordCancellationDialog";
import { RecordDespatchDialog } from "../app/components/domain/orders/RecordDespatchDialog";
import { PurchaseOrderDetailsPanel } from "../app/components/domain/orders/DetailsPanels";
import {
  PurchaseOrderLineSupplier,
  hasSupplierSide,
} from "../app/components/domain/purchasing/PurchaseOrderLineSupplier";
import { SentToSupplierLine } from "../app/components/domain/purchasing/SentToSupplierLine";
import {
  SubjectAlerts,
  useAlertSheet,
} from "../app/components/domain/needs-attention/SubjectAlerts";
import { useSubjectAlerts } from "@shared-core/domain/useSubjectAlerts";
import {
  ActivityPanel,
  HealthPill,
  StatusPill,
  TabCard,
} from "../app/components/domain/orders/OrderPanels";

const pounds = formatQuotedPrice;

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "not_found" }
  | { kind: "loaded"; po: PurchaseOrderView };

const TAB_LABELS: Record<PurchaseOrderTab, string> = {
  now: "Now",
  details: "Details",
  lines: "Lines",
  receipts: "Receipts",
  "purchase-invoices": "Purchase invoices",
  activity: "Activity",
};

/** The purchase order's state: green once it has all arrived. */
function PurchaseOrderStatusPill({ po }: { po: PurchaseOrderView }) {
  const finished = po.status === "delivered" || po.status === "received";
  return (
    <Badge
      variant={
        finished
          ? "success"
          : po.status === "cancelled"
            ? "neutral"
            : "secondary"
      }
    >
      {po.status_label}
    </Badge>
  );
}

function findDeliveryLine(
  po: PurchaseOrderView,
  id: string,
): DeliveryLineView | null {
  for (const line of po.lines) {
    const found = line.delivery_lines.find((d) => d.id === id);
    if (found) return found;
  }
  return null;
}

export function PurchaseOrderDetailPage() {
  const { purchaseOrderNo: rawNo, tab: rawTab } = useParams<{
    purchaseOrderNo: string;
    tab?: string;
  }>();
  const no = rawNo ? decodeURIComponent(rawNo) : "";
  const tab: PurchaseOrderTab | null = !rawTab
    ? "now"
    : (PURCHASE_ORDER_TABS as readonly string[]).includes(rawTab)
      ? (rawTab as PurchaseOrderTab)
      : null;
  const adapter = useDataAdapter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Needs attention on this purchase order (v1 completion I §10).
  const subjectAlerts = useSubjectAlerts({ purchaseOrderNo: no });
  const alertSheet = useAlertSheet();
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState<DeliveryLineView | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isApprovingPrice, setIsApprovingPrice] = useState(false);
  const [isRecordingDespatch, setIsRecordingDespatch] = useState(false);
  const [agreeing, setAgreeing] = useState<PurchaseOrderLineView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Why a supplier-interface step was refused (supplier interface spec 09 §3).
  const [failure, setFailure] = useState<string | null>(null);
  const capabilities = usePlatformCapabilities();

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    adapter
      .getPurchaseOrder(no)
      .then((po) => {
        if (cancelled) return;
        setState(po ? { kind: "loaded", po } : { kind: "not_found" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: "failed",
          message:
            err instanceof Error
              ? err.message
              : "Could not load the purchase order.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, no]);

  const record = useCallback(
    async (
      po: PurchaseOrderView,
      line: DeliveryLineView,
      completion: LineCompletion,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyPurchaseOrderCommand(
          po,
          recordCommand(line, completion),
        );
        setState({ kind: "loaded", po: next });
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  const recordCancellation = useCallback(
    async (
      po: PurchaseOrderView,
      input: RecordPurchaseOrderCancellationRequest,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyPurchaseOrderCommand(po, {
          kind: "record_cancellation",
          input,
        });
        setState({ kind: "loaded", po: next });
        setNotice(
          next.status === "cancelled"
            ? `Recorded ${po.purchase_order_no} as cancelled.`
            : `Recorded ${input.purchase_order_line_ids?.length === 1 ? "a line" : "lines"} of ${po.purchase_order_no} as cancelled.`,
        );
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  const approvePrice = useCallback(
    async (
      po: PurchaseOrderView,
      input: ApprovePurchaseOrderPriceRequest,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyPurchaseOrderCommand(po, {
          kind: "approve_price",
          input,
        });
        setState({ kind: "loaded", po: next });
        setNotice(`Approved the price on ${po.purchase_order_no}.`);
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  const recordAgreedAmount = useCallback(
    async (
      po: PurchaseOrderView,
      line: PurchaseOrderLineView,
      input: RecordAgreedAmountRequest,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyPurchaseOrderCommand(po, {
          kind: "record_agreed_amount",
          lineNo: line.line_no,
          input,
        });
        setState({ kind: "loaded", po: next });
        setNotice(`Recorded the agreed amount on ${line.line_reference}.`);
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  const recordDespatch = useCallback(
    async (
      po: PurchaseOrderView,
      input: RecordPurchaseOrderDespatchRequest,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      try {
        const next = await adapter.applyPurchaseOrderCommand(po, {
          kind: "record_despatch",
          input,
        });
        setState({ kind: "loaded", po: next });
        setNotice(`Recorded ${po.purchase_order_no} as despatched.`);
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  /** A supplier-interface step on the purchase order (resend, revoke, a date, a proof), then the page re-read. */
  const supplierStep = useCallback(
    async (
      po: PurchaseOrderView,
      command: PurchaseOrderCommand,
      done: string,
    ) => {
      setBusy(true);
      setNotice(null);
      setFailure(null);
      try {
        const next = await adapter.applyPurchaseOrderCommand(po, command);
        setState({ kind: "loaded", po: next });
        setNotice(done);
      } catch (err) {
        setFailure(orderStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  /** A supplier's proof, fetched with the caller's auth and saved. */
  const openProof = useCallback(
    async (proof: SupplierProofView) => {
      setFailure(null);
      try {
        const { blob, filename } = await adapter.downloadSupplierProof(proof);
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setFailure(orderStepFailure(err).summary);
      }
    },
    [adapter, capabilities],
  );

  if (state.kind === "loading") {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Loading purchase order…</div>
      </div>
    );
  }
  if (state.kind === "failed") {
    return (
      <Alert variant="destructive" role="alert">
        <AlertDescription>
          <p>
            Could not load purchase order {no}: {state.message}
          </p>
        </AlertDescription>
      </Alert>
    );
  }
  if (state.kind === "not_found" || tab === null) {
    return (
      <Card className="p-12 text-center">
        <h1 className="mb-2 text-lg">
          {tab === null ? "Page not found" : "Purchase order not found"}
        </h1>
        <p className="text-muted-foreground">
          {tab === null
            ? `Purchase order ${no} has no “${rawTab}” tab.`
            : `There is no purchase order ${no} that you can see. Check the number, or open it from its order.`}
        </p>
      </Card>
    );
  }

  const { po } = state;
  const supplier = po.supplier.name ?? po.supplier.supplier_no ?? "Supplier";
  // Ben, 10 Oct: one line in the header on every tab, not mock-up 11's panel.
  const sentLine = po.sent_to_supplier && (
    <SentToSupplierLine
      sent={po.sent_to_supplier}
      isBusy={busy}
      onResend={() =>
        supplierStep(
          po,
          { kind: "resend_order_email" },
          `Sent ${po.purchase_order_no} to ${supplier} again, with a new link.`,
        )
      }
      onRevoke={() =>
        supplierStep(
          po,
          { kind: "revoke_order_links" },
          `Revoked every emailed link to ${po.purchase_order_no}.`,
        )
      }
    />
  );
  const lineSupplier = (line: PurchaseOrderLineView) => (
    <PurchaseOrderLineSupplier
      line={line}
      supplier={supplier}
      isBusy={busy}
      onOpenProof={openProof}
      onDecideDate={(input) =>
        supplierStep(
          po,
          { kind: "decide_date_change", lineNo: line.line_no, input },
          input.outcome === "accepted"
            ? `Accepted the new date for line ${line.line_no}.`
            : `Line ${line.line_no} goes back to To place for another supplier.`,
        )
      }
      onDecideProof={(proof, input) =>
        supplierStep(
          po,
          { kind: "decide_supplier_proof", proofId: proof.id, input },
          input.outcome === "approved"
            ? `Approved the proof for line ${line.line_no}.`
            : `Asked ${supplier} for changes to the proof for line ${line.line_no}.`,
        )
      }
    />
  );
  return (
    // Room at the foot so the floating Feedback button never covers a row's step.
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <ObjectHeader
        crumbs={[
          { label: "Suppliers" },
          { label: supplier },
          { label: po.purchase_order_no },
        ]}
        title={`Purchase order ${po.purchase_order_no}`}
        subtitle={sentLine || undefined}
        pills={
          <>
            <PurchaseOrderStatusPill po={po} />
            {po.acknowledgement_label && (
              <Badge variant="warning">{po.acknowledgement_label}</Badge>
            )}
            <HealthPill health={po.health} label={po.health_label} />
          </>
        }
        counterpart={supplier}
        value={pounds(po.cost_pence)}
        valueLabel="cost, ex VAT"
        facts={[
          {
            label: po.lines.length === 1 ? "Line" : "Lines",
            value: po.lines_label,
          },
          ...(po.supplier_reference
            ? [
                {
                  label: po.supplier_reference_caption,
                  value: po.supplier_reference,
                },
              ]
            : []),
          // Doc 54 §7: when the supplier should despatch, until it has.
          ...(po.despatch_due_on && !po.despatch
            ? [
                {
                  label: "Despatch due",
                  value: formatShortDateOrDash(po.despatch_due_on),
                },
              ]
            : []),
        ]}
        actions={
          (po.record_despatch ||
            po.record_cancellation ||
            po.approve_price_url) && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" aria-label="More actions">
                  <MoreHorizontal aria-hidden className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {po.approve_price_url && (
                  <DropdownMenuItem onSelect={() => setIsApprovingPrice(true)}>
                    Approve price…
                  </DropdownMenuItem>
                )}
                {po.record_despatch && (
                  <DropdownMenuItem
                    onSelect={() => setIsRecordingDespatch(true)}
                  >
                    Record despatch…
                  </DropdownMenuItem>
                )}
                {po.record_cancellation && (
                  <DropdownMenuItem onSelect={() => setIsCancelling(true)}>
                    Record as already cancelled with the supplier…
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {failure && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}

      <SubjectAlerts
        alerts={subjectAlerts.alerts}
        subjectRef={po.purchase_order_no}
        onChanged={subjectAlerts.reload}
        {...alertSheet}
      />

      {/* Padded as the job page's stage rail card is. */}
      <Card>
        <CardContent className="py-3">
          <LifecycleRail
            rail={railFromPurchaseOrder(po)}
            ariaLabel={`Where purchase order ${po.purchase_order_no} is`}
          />
        </CardContent>
      </Card>

      <ObjectTabs
        ariaLabel={`Purchase order ${po.purchase_order_no}`}
        current={tab}
        tabs={PURCHASE_ORDER_TABS.map((t) => ({
          id: t,
          label: TAB_LABELS[t],
          to: paths.purchaseOrderDetail({
            purchaseOrderNo: po.purchase_order_no,
            tab: t,
          }),
          badge: t === "now" ? nowBadge(po.now) : null,
        }))}
      />

      {tab === "now" && (
        <div>
          <div className="min-w-0 space-y-4">
            <OrderNowPanel
              items={po.now}
              busy={busy}
              onRecord={(id) => setRecording(findDeliveryLine(po, id))}
              placementHref={
                po.placement_url
                  ? paths.placement({ purchaseOrderNo: po.purchase_order_no })
                  : undefined
              }
            />
            {po.lines.some(
              (l) => l.date_proposal || l.proofs.some((p) => p.decide_url),
            ) && (
              <section aria-label="Waiting on you" className="space-y-3">
                {po.lines
                  .filter(
                    (l) =>
                      l.date_proposal || l.proofs.some((p) => p.decide_url),
                  )
                  .map((l) => (
                    <TabCard
                      key={l.line_no}
                      title={`Line ${l.line_no} · ${l.description}`}
                    >
                      {lineSupplier(l)}
                    </TabCard>
                  ))}
              </section>
            )}
          </div>
        </div>
      )}
      {tab === "details" && <PurchaseOrderDetailsPanel po={po} />}
      {tab === "lines" && (
        <PurchaseOrderLinesTable
          po={po}
          lineSupplier={lineSupplier}
          busy={busy}
          onRequestRecord={setRecording}
          onAgree={setAgreeing}
        />
      )}
      {tab === "receipts" && (
        <TabCard>
          <DeliveryLinesTable
            busy={busy}
            onRequestRecord={setRecording}
            rows={po.lines.flatMap((l) =>
              l.delivery_lines.map((line) => ({
                line,
                ownerLabel:
                  po.lines.length > 1 ? `Line ${l.line_no}` : undefined,
              })),
            )}
          />
        </TabCard>
      )}
      {tab === "purchase-invoices" && <PurchaseOrderPurchaseInvoices po={po} />}
      {tab === "activity" && <ActivityPanel entries={po.history} />}

      {isRecordingDespatch && po.record_despatch && (
        <RecordDespatchDialog
          purchaseOrderNo={po.purchase_order_no}
          supplierName={supplier}
          despatch={po.record_despatch}
          busy={busy}
          onClose={() => setIsRecordingDespatch(false)}
          onRecord={(input) => recordDespatch(po, input)}
        />
      )}

      {isApprovingPrice && po.approve_price_url && (
        <ApprovePriceDialog
          purchaseOrderNo={po.purchase_order_no}
          supplierName={supplier}
          costPence={po.cost_pence}
          busy={busy}
          onClose={() => setIsApprovingPrice(false)}
          onApprove={(input) => approvePrice(po, input)}
        />
      )}

      {isCancelling && po.record_cancellation && (
        <RecordCancellationDialog
          purchaseOrderNo={po.purchase_order_no}
          supplierName={supplier}
          isPlaced={po.milestones.placed.at !== null}
          cancellation={po.record_cancellation}
          busy={busy}
          onClose={() => setIsCancelling(false)}
          onRecord={(input) => recordCancellation(po, input)}
        />
      )}

      {agreeing?.record_agreed_amount && (
        <RecordAgreedAmountDialog
          agreed={agreeing.record_agreed_amount}
          busy={busy}
          onClose={() => setAgreeing(null)}
          onRecord={(input) => recordAgreedAmount(po, agreeing, input)}
        />
      )}

      {recording && (
        <LineCompletionDialog
          key={recording.id}
          line={recording}
          busy={busy}
          onClose={() => setRecording(null)}
          onRecord={(completion) => record(po, recording, completion)}
        />
      )}
    </div>
  );
}

/** What the line is for: its order line's chip. */
function ForChip({ line }: { line: PurchaseOrderLineView }) {
  const f = line.for;
  if (!f.order_no) {
    // An estimate's purchase order line, recorded before orders existed.
    return (
      <ObjectChip
        label="An estimate"
        detail={f.customer_name ?? f.customer_code ?? undefined}
      />
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <ObjectChip
        label={`Order ${f.order_no}${f.order_line_no ?? ""}`}
        detail={f.customer_name ?? f.customer_code ?? undefined}
        to={paths.orderDetail({
          orderNo: f.order_no,
          step: "order",
          line: f.order_line_no ?? undefined,
        })}
      />
      {f.into_stock_item_ref && (
        <InfoTip label={`About line ${line.line_no}`}>
          Into stock item {f.into_stock_item_ref}.
        </InfoTip>
      )}
    </span>
  );
}

/** A part of the line on its way or arrived: the line reads by its parts (orders' #2748). */
const IN_PARTS = new Set(["part_sent", "sent", "part_delivered"]);

function PurchaseOrderLinesTable({
  po,
  lineSupplier,
  busy,
  onRequestRecord,
  onAgree,
}: {
  po: PurchaseOrderView;
  /** A line's side of the supplier interface, under its row when it has one. */
  lineSupplier: (line: PurchaseOrderLineView) => ReactNode;
  busy: boolean;
  /** A delivery's Record delivered / received. */
  onRequestRecord: (line: DeliveryLineView) => void;
  /** Record agreed amount on a cut line (doc 98 §5.2). */
  onAgree: (line: PurchaseOrderLineView) => void;
}) {
  const hasDeliveries = (line: PurchaseOrderLineView) =>
    (line.despatch_progress?.deliveries.length ?? 0) > 0;
  return (
    <TabCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>For</TableHead>
            <TableHead>What</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Cost</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {po.lines.map((line) => (
            <Fragment key={line.line_no}>
              <TableRow
                className={
                  hasSupplierSide(line) || hasDeliveries(line)
                    ? "border-b-0"
                    : undefined
                }
              >
                <TableCell className="tabular-nums text-muted-foreground">
                  {line.line_no}
                </TableCell>
                <TableCell>
                  <ForChip line={line} />
                </TableCell>
                <TableCell className="max-w-sm whitespace-normal font-medium">
                  {line.description}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.quantity.toLocaleString("en-GB")}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {pounds(line.committed_value_pence)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill tone={line.status.tone}>
                      {line.despatch_progress &&
                      line.state === "open" &&
                      IN_PARTS.has(line.despatch_progress.state)
                        ? despatchProgressStatus(line.despatch_progress)
                        : line.status.label}
                    </StatusPill>
                    {line.cancelled_label && (
                      <span className="text-sm text-muted-foreground">
                        {line.cancelled_label}
                      </span>
                    )}
                    {line.record_agreed_amount && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Record agreed amount on ${line.line_reference}`}
                        onClick={() => onAgree(line)}
                      >
                        Record agreed amount…
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              {(hasSupplierSide(line) || hasDeliveries(line)) && (
                <TableRow>
                  <TableCell />
                  <TableCell
                    colSpan={5}
                    className="space-y-2 whitespace-normal pt-0"
                  >
                    {hasSupplierSide(line) && lineSupplier(line)}
                    {line.despatch_progress && (
                      <LineDespatches
                        progress={line.despatch_progress}
                        deliveryLines={line.delivery_lines}
                        busy={busy}
                        onRequestRecord={onRequestRecord}
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={4} className="text-right font-medium">
              Total cost, ex VAT
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {pounds(po.cost_pence)}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </TabCard>
  );
}

const MATCH_STATUS: Record<
  SupplierPurchaseInvoice["match_state"],
  { tone: "neutral" | "done"; label: string }
> = {
  matched: { tone: "done", label: "Matched" },
  not_matched: { tone: "neutral", label: "Not matched yet" },
};

/** The supplier's purchase invoices against it, and whether each is matched. */
function PurchaseOrderPurchaseInvoices({ po }: { po: PurchaseOrderView }) {
  const info = po.purchase_invoices_info;
  const totals = po.purchase_invoice_totals;
  if (po.purchase_invoices.length === 0 || !totals) {
    return (
      <TabCard
        title="Purchase invoices"
        info={info}
        infoLabel="How purchase invoices are matched"
      >
        <p className="text-sm text-muted-foreground">
          {po.no_purchase_invoice_sentence}
        </p>
      </TabCard>
    );
  }
  return (
    <TabCard
      title="Purchase invoices"
      info={info}
      infoLabel="How purchase invoices are matched"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Purchase invoice</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Net</TableHead>
            <TableHead className="text-right">VAT</TableHead>
            <TableHead className="text-right">Gross</TableHead>
            <TableHead>Match status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {po.purchase_invoices.map((b) => (
            <TableRow key={b.reference}>
              <TableCell className="font-medium tabular-nums">
                {b.reference}
              </TableCell>
              <TableCell className="tabular-nums">
                {formatShortDateOrDash(b.invoice_date)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {pounds(b.net_pence)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {pounds(b.vat_pence)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {pounds(b.gross_pence)}
              </TableCell>
              <TableCell>
                <StatusPill tone={MATCH_STATUS[b.match_state].tone}>
                  {MATCH_STATUS[b.match_state].label}
                </StatusPill>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2} className="font-medium">
              Total billed
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {pounds(totals.net_pence)}
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {pounds(totals.vat_pence)}
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {pounds(totals.gross_pence)}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </TabCard>
  );
}
